import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { CACHE } from '../api/cache';

// The caller tenant's enabled modules, for hiding nav the plan does not license. Reads
// /v1/entitlements (a tenant-scoped read of tenant_entitlement). An unconfigured tenant returns
// no rows → null → every module is visible (matches the backend gate's fail-open). On the admin
// engine (prefix /v1/admin, where there is no entitlements resource) the request 404s → null too,
// so the admin nav is never filtered. Never blocks the shell: any error resolves to "show all".

const CORE = new Set(['platform', 'finance', 'home', 'feedback', 'support']);

export function useEntitlements(): { isVisible: (moduleName: string) => boolean } {
  const api = useApi();
  const { data } = useQuery<Set<string> | null>({
    queryKey: ['entitlements', api.prefix],
    ...CACHE.session,
    retry: false,
    queryFn: async () => {
      try {
        const res = await api.request<{ data: { module_name: string; enabled: boolean }[] }>(`${api.prefix}/entitlements`);
        if (!res.data || res.data.length === 0) return null; // unconfigured → all visible
        return new Set(res.data.filter((e) => e.enabled).map((e) => e.module_name));
      } catch {
        return null; // 404 / network / non-tenant surface → do not filter
      }
    },
  });
  return {
    isVisible: (moduleName: string) => CORE.has(moduleName) || data == null || data.has(moduleName),
  };
}
